/**
 * Band ↔ score conversion for AI-enriched timeline events.
 *
 * Two representations, on purpose, because the two ends want different things:
 *
 *  - The MODEL is asked for a band (`high` / `medium` / `low`). Asked for a raw
 *    0..1 number instead it clusters everything at 0.8–0.9 and the filter stops
 *    discriminating; three labels force an actual choice.
 *  - The DOCUMENT stores `TimelineEvent.aiConfidence` as a number, which is what
 *    shared-types declares and what leaves room to change the thresholds later
 *    without rewriting stored data.
 *
 * So the callable maps band → score on the way in, and the review dialog maps
 * score → band on the way out. Both directions live here, and the same table is
 * mirrored in `functions/src/enrichManufacturerHistory.ts` — functions is a
 * separate package and cannot import from `src/`, the same arrangement as
 * `functions/src/calibreKey.ts` ↔ `services/calibreResolution.ts`. Changes here
 * MUST be mirrored there.
 */

export const TIMELINE_CONFIDENCE_BANDS = ['high', 'medium', 'low'] as const;
export type TimelineConfidenceBand = (typeof TIMELINE_CONFIDENCE_BANDS)[number];

/**
 * Score written for each band. Spaced so each lands mid-way inside its own
 * threshold window rather than on a boundary — a band that scored exactly at
 * its cutoff would flip category on any later tweak to the thresholds.
 */
export const TIMELINE_CONFIDENCE_SCORES: Record<TimelineConfidenceBand, number> = {
  high: 0.9,
  medium: 0.6,
  low: 0.3,
};

/** Threshold at or above which a score reads as its band. */
const HIGH_AT = 0.8;
const MEDIUM_AT = 0.5;

export function scoreForBand(band: string | undefined): number {
  const key = (band ?? '').trim().toLowerCase() as TimelineConfidenceBand;
  return TIMELINE_CONFIDENCE_SCORES[key] ?? TIMELINE_CONFIDENCE_SCORES.medium;
}

/**
 * The band a stored score reads as.
 *
 * A missing score reads as `medium`, matching what `enrichReference` does with
 * a confidence the model omitted: the alternative is for an unlabelled event to
 * silently vanish from every filter, which is the failure mode that made
 * unchecked rows look like dropped suggestions.
 */
export function bandForScore(score: number | undefined | null): TimelineConfidenceBand {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 'medium';
  if (score >= HIGH_AT) return 'high';
  if (score >= MEDIUM_AT) return 'medium';
  return 'low';
}

/** What the dialog's confidence selector offers. */
export type TimelineConfidenceFilter = 'high' | 'highMedium' | 'all';

export const TIMELINE_CONFIDENCE_FILTER_LABELS: Record<TimelineConfidenceFilter, string> = {
  high: 'High only',
  highMedium: 'High + Medium',
  all: 'All (including low)',
};

/** Does this event's score pass the chosen filter? */
export function meetsTimelineConfidence(
  score: number | undefined | null,
  filter: TimelineConfidenceFilter,
): boolean {
  if (filter === 'all') return true;
  const band = bandForScore(score);
  return filter === 'high' ? band === 'high' : band === 'high' || band === 'medium';
}

/** Human label for a score, for the per-event badge. */
export function confidenceLabel(score: number | undefined | null): string {
  const band = bandForScore(score);
  return typeof score === 'number' && Number.isFinite(score)
    ? `${band} (${score.toFixed(2)})`
    : band;
}
